import type { Logger } from '@mimir/common/types/logger';
import type { Fraction } from '@mimir/common/util/fractionType';
import type { CommandFromMain, MessageFromMain, MessageFromWorker, RequestFromMain, ResponseFromWorker, StatusUpdate } from './cutterMediaPlayerProtocol';
import { invariant } from '@mimir/common/util/invariant';
import type { PlaybackQuality, QualityOption } from '@mimir/common/types/playbackQuality';
import type { AudioHandler } from '@mimir/cutter/player/audioHandler';
import { createItemSegmentMainHandler } from '../worker/mainHandlers/itemSegmentMainHandler';
import type { ItemSegmentMessageFromMain } from '../worker/protocol/itemSegmentProtocol';
import { createAudioMainHandler } from '../worker/mainHandlers/audioMainHandler';
import type { AudioRequestFromMain } from '../worker/protocol/audioProtocol';
import type { BufferCache } from '@mimir/common/util/bufferCache';
import type { BufferRange } from '@mimir/cutter/frameProvider/frameProvider';
import type { VisualizationMode } from '../audioVisualizer';
import type { AudioLevelsData } from '@mimir/cutter/player/cutterCorePlayer';
import { createLutMainHandler } from '../worker/mainHandlers/lutMainHandler';
import type { LutMessageFromMain } from '../worker/protocol/lutProtocol';
import type { Lut } from '@mimir/common/types/lut';
import { deserializeError } from '../worker/protocol/serializedError';
import type { SegmentProviderFactory } from '../segmentProviderFactory';
import type { PlayableArtifactAutoSelectHint } from '@mimir/common/util/selectPlayableArtifact';

export interface LoadItemResult {
  duration: Fraction,
  frameRate: Fraction,
  videoWidth: number | null,
  videoHeight: number | null,
  quality: PlaybackQuality | null,
  qualities: QualityOption[],
  currentLut: string | null,
  availableLuts: LutChoice[],
}

export interface SetQualityResult {
  quality: PlaybackQuality,
  qualities: QualityOption[],
}

export interface LutChoice {
  id: string | null,
  title: string,
}

export interface QualitySelectionOptions {
  /**
   *  Try to load this quality instead of the default one. If the quality
   *  fails to load or specific quality is not set then the player will
   *  auto select another quality based on the autoSelectHint
   */
  specificQuality: PlaybackQuality | null,

  /**
   *  Determines how the player select default and fallback quality. See
   *  documentation for PlayableArtifactAutoSelectHint for description of
   *  the various hints.
   */
  autoSelectHint: PlayableArtifactAutoSelectHint,
}

export interface MediaPlayer {
  loadItem(
    itemId: string,
    qualitySelectionOptions: QualitySelectionOptions,
    ignoreVideo: boolean,
    ignoreAudio: boolean,
    initialMuteStates: boolean[]
  ): Promise<LoadItemResult>;
  setQuality(
    quality: PlaybackQuality | null,
    autoSelectHint: PlayableArtifactAutoSelectHint,
    channelMuteStates: boolean[]
  ): Promise<SetQualityResult>;
  setPosition(position: Fraction): Promise<void>;
  setPlaybackRate(speedFactor: Fraction): void;
  stepOneFrameBackward(): void;
  stepOneFrameForward(): void;
  setAudioTrackMuteState(trackIndex: number, muted: boolean): void;
  setAudioVisualizationMode(mode: VisualizationMode | null): void;
  setVolume(volume: number): void;
  setMuted(muted: boolean): void;
  setClampedPlaybackRange(range: { start: Fraction, end: Fraction } | null): void;

  /**
   *  Set the enabled LUTs. The player will pick the appropriate LUT from this
   *  list. If there are several LUTs with the same conversion the first one
   *  will be picked. To disable LUT conversion pass in an empty list. Use the
   *  availableLuts array returned from loadItem to obtain possible ids. Ids
   *  not in the availableLuts array will be ignored.
   */
  setEnabledLuts(ids: string[]): Promise<string | null>;
  clear(): void;
  /**
   * Terminate the worker, stop the resolution polling loop, and reject
   * any in-flight requests. After `destroy()` returns, further method
   * calls produce undefined behaviour (typically silent no-ops or
   * rejected promises) — the caller is expected to drop the reference.
   * Idempotent.
   */
  destroy(): void;
}

export interface MediaPlayerListener {
  onPlay(): void,
  onPause(): void,
  onPositionChanged(position: Fraction): void,
  onDurationChanged(duration: Fraction): void,
  onBuffered(bufferedRanges: BufferRange[]): void,
  onMuteStatesChanged(muteStates: boolean[]): void,
  onAudioVisualizationModeChanged(mode: VisualizationMode | null, available: (VisualizationMode | null)[]): void;
  onAudioLevelsData(data: AudioLevelsData): void;
  onVirtualClipRangeChanged(range: { start: Fraction, end: Fraction } | null): void;
  onPlayerError(message: string): void;
}

export interface CutterMediaPlayerServices {
  bufferCache: BufferCache,
  logger: Logger,
  createSegmentProviderForItem: SegmentProviderFactory,
  getLuts: () => Promise<Lut[]>,
}

export interface CutterMediaPlayerSettings {
  prematureVideoFrameReuseBugWorkaround: boolean,
  forceSystemMemoryBuffers: boolean,
  forceSoftwareDecoder: boolean,
}

export interface CutterMediaPlayerParameters {
  services: CutterMediaPlayerServices,
  listener: MediaPlayerListener,
  workerProcessorUrl: string,
  canvas: HTMLCanvasElement,
  audioHandler: AudioHandler,
  settings: CutterMediaPlayerSettings,
}

export function createCutterMediaPlayer(parameters: CutterMediaPlayerParameters): MediaPlayer {
  return createCutterMediaPlayerPrivate(parameters);
}

interface PendingRequest {
  resolve: (response: ResponseFromWorker) => void,
  reject: (error: unknown) => void,
}

function createCutterMediaPlayerPrivate(parameters: CutterMediaPlayerParameters): MediaPlayer {
  const { canvas, services, listener, workerProcessorUrl, audioHandler, settings } = parameters;
  const { prematureVideoFrameReuseBugWorkaround, forceSystemMemoryBuffers, forceSoftwareDecoder } = settings;
  const { bufferCache, logger, createSegmentProviderForItem, getLuts } = services;

  const worker = new Worker(workerProcessorUrl, { type: 'module' });

  // destroy() flips this to true. Gates the resolution-polling loop and
  // postMessage paths so a destroyed player doesn't keep talking to a
  // terminated worker.
  let destroyed = false;

  const postMessageFromMain = (message: MessageFromMain, transfer?: Transferable[]) => {
    if (destroyed) return;
    if (transfer !== undefined) {
      worker.postMessage(message, { transfer });
    } else {
      worker.postMessage(message);
    }
  };

  const updateResolution = () => {
    if (destroyed) return;
    const rect = canvas.getBoundingClientRect();
    const height = rect.height * window.devicePixelRatio;
    const width = rect.width * window.devicePixelRatio;
    // If the player is hidden it will have size of zero. Let's not confuse the
    // player by passing in nonsense values
    if ((width !== 0) && (height !== 0)) {
      postMessageFromMain({
        type: 'command',
        command: {
          type: 'update_display_resolution',
          width,
          height,
        }
      });
    }
    setTimeout(updateResolution, 100);
  };

  const postCommandFromMain = (command: CommandFromMain, transfer?: Transferable[]): void => {
    postMessageFromMain({ type: 'command', command }, transfer);
  };

  let requestIdCounter : number = 0;
  const pendingRequests = new Map<number, PendingRequest>();

  const postRequestFromMain = async (request: RequestFromMain): Promise<ResponseFromWorker> => {
    const requestId = requestIdCounter++;
    return await new Promise<ResponseFromWorker>((resolve, reject) => {
      pendingRequests.set(requestId, { resolve, reject });
      postMessageFromMain({
        type: 'request',
        requestId,
        request,
      });
    });
  };

  const handleWorkerResponse = (requestId: number, response: ResponseFromWorker) => {
    const pendingRequest = pendingRequests.get(requestId);
    if (pendingRequest === undefined) {
      logger.warn(`Unexpected response for requestId: ${requestId}`);
      return;
    }
    pendingRequests.delete(requestId);
    if (response.type === 'error') {
      pendingRequest.reject(deserializeError(response.error));
    } else {
      pendingRequest.resolve(response);
    }
  };

  const handleItemSegmentRequestFromWorker =
    createItemSegmentMainHandler(
      createSegmentProviderForItem,
      (message: ItemSegmentMessageFromMain) => {
        postMessageFromMain({ type: 'item_segment', message });
      },
    );

  const handleAudioRequestFromWorker =
    createAudioMainHandler(
      { bufferCache },
      audioHandler,
      (request: AudioRequestFromMain, transfer?: Transferable[]): void => {
        postMessageFromMain({ type: 'audio', request }, transfer);
      }
    );

  const handleLutRequestFromWorker =
    createLutMainHandler(
      getLuts,
      (message: LutMessageFromMain, transfer?: Transferable[]): void => {
        postMessageFromMain({ type: 'lut', message }, transfer);
      }
    );

  const handleStatusUpdateFromWorker = (statusUpdate: StatusUpdate) => {
    const statusUpdateType = statusUpdate.type;
    switch (statusUpdateType) {
      case 'playing': {
        if (statusUpdate.playing) {
          listener.onPlay();
        } else {
          listener.onPause();
        }
        break;
      }
      case 'position': {
        listener.onPositionChanged(statusUpdate.position);
        break;
      }
      case 'duration_changed': {
        listener.onDurationChanged(statusUpdate.duration);
        break;
      }
      case 'buffered': {
        listener.onBuffered(statusUpdate.bufferedRanges);
        break;
      }
      case 'mute_states': {
        listener.onMuteStatesChanged(statusUpdate.muteStates);
        break;
      }
      case 'audio_visualization_mode': {
        listener.onAudioVisualizationModeChanged(statusUpdate.mode, statusUpdate.available);
        break;
      }
      case 'audio_levels_data': {
        listener.onAudioLevelsData(statusUpdate.data);
        break;
      }
      case 'virtual_clip_range': {
        listener.onVirtualClipRangeChanged(statusUpdate.range);
        break;
      }
      case 'player_error': {
        listener.onPlayerError(statusUpdate.message);
        break;
      }
      default:
        statusUpdateType satisfies never;
    }
  };

  worker.onmessage = ((event: MessageEvent<MessageFromWorker>) => {
    const message = event.data;
    const messageType = message.type;
    switch (messageType) {
      case 'response': {
        handleWorkerResponse(message.requestId, message.response);
        break;
      }
      case 'item_segment': {
        handleItemSegmentRequestFromWorker(message.message);
        break;
      }
      case 'audio': {
        handleAudioRequestFromWorker(message.request);
        break;
      }
      case 'status_update': {
        handleStatusUpdateFromWorker(message.statusUpdate);
        break;
      }
      case 'lut': {
        handleLutRequestFromWorker(message.message);
        break;
      }
      default:
        messageType satisfies never;
    }
  });

  const offscreenCanvas = canvas.transferControlToOffscreen();
  postCommandFromMain(
    {
      type: 'initialize',
      offscreenCanvas,
      prematureVideoFrameReuseBugWorkaround,
      forceSystemMemoryBuffers,
      forceSoftwareDecoder,
    },
    [offscreenCanvas]
  );

  updateResolution();

  return {
    loadItem: async (
      itemId: string,
      qualitySelectionOptions: QualitySelectionOptions,
      ignoreVideo: boolean,
      ignoreAudio: boolean,
      initialMuteStates: boolean[]
    ): Promise<LoadItemResult> => {
      const response =
        await postRequestFromMain({
          type: 'load_item',
          itemId,
          specificQuality: qualitySelectionOptions.specificQuality,
          autoSelectHint: qualitySelectionOptions.autoSelectHint,
          ignoreVideo,
          ignoreAudio,
          initialMuteStates
        });

      invariant(response.type === 'item_loaded', `Unexpected response from worker: ${response.type}`);
      return {
        duration: response.duration,
        frameRate: response.frameRate,
        videoWidth: response.videoWidth,
        videoHeight: response.videoHeight,
        quality: response.quality,
        qualities: response.qualities,
        currentLut: response.currentLut,
        availableLuts: response.availableLuts,
      };
    },
    setQuality: async (
      quality: PlaybackQuality,
      autoSelectHint: PlayableArtifactAutoSelectHint,
      channelMuteStates: boolean[]
    ): Promise<SetQualityResult> => {
      const response = await postRequestFromMain({ type: 'set_quality', quality, autoSelectHint, channelMuteStates });
      invariant(response.type === 'quality_changed', `Unexpected response from worker: ${response.type}`);
      return {
        quality: response.quality,
        qualities: response.qualities,
      };
    },
    setPosition: async (position: Fraction): Promise<void> => {
      const response = await postRequestFromMain({ type: 'set_position', position });
      invariant(response.type === 'ok', `Unexpected response from worker: ${response.type}`);
    },
    setPlaybackRate(speedFactor: Fraction): void {
      postCommandFromMain({ type: 'set_playback_rate', speedFactor });
    },
    stepOneFrameBackward(): void {
      postCommandFromMain({ type: 'step_one_frame_backward' });
    },
    stepOneFrameForward(): void {
      postCommandFromMain({ type: 'step_one_frame_forward' });
    },
    setAudioTrackMuteState(trackIndex: number, muted: boolean): void {
      postCommandFromMain({ type: 'set_audio_track_mute_state', trackIndex, muted });
    },
    setAudioVisualizationMode(mode: VisualizationMode): void {
      postCommandFromMain({ type: 'set_audio_visualization_mode', mode });
    },
    setVolume(volume: number): void {
      postCommandFromMain({ type: 'set_volume', volume });
    },
    setMuted(muted: boolean): void {
      postCommandFromMain({ type: 'set_muted', muted });
    },
    setClampedPlaybackRange(range: { start: Fraction, end: Fraction } | null): void {
      postCommandFromMain({ type: 'set_clamped_playback_range', range });
    },
    async setEnabledLuts(ids: string[]): Promise<string | null> {
      const response = await postRequestFromMain({ type: 'set_enabled_luts', ids });
      invariant(response.type === 'lut_changed', `Unexpected response from worker: ${response.type}`);
      return response.lutId;
    },
    clear(): void {
      postCommandFromMain({ type: 'clear' });
    },
    destroy(): void {
      if (destroyed) return;
      destroyed = true;
      // Reject any callers awaiting a worker response before terminate(),
      // otherwise their promises hang forever. The terminate below stops
      // the worker mid-task without delivering replies.
      const inFlight = Array.from(pendingRequests.values());
      pendingRequests.clear();
      for (const req of inFlight) {
        req.reject(new Error('MediaPlayer destroyed before worker responded'));
      }
      // Detach the message handler before terminate so a late event
      // queued by the platform doesn't try to dispatch into closed state.
      worker.onmessage = null;
      worker.terminate();
    },
  };
}

